/**
 * Portfolio Analysis ("Bestandsanalyse") — the advisor-wide AI potential overview served by the
 * `ai` service under `/ai/portfolio`. Mirrors the backend DTOs (FIN-7097). The scan pre-screens
 * every client without a model call and sends only the clients matching the chosen scope to the
 * model; results are persisted and never recomputed on read.
 */

import { AnalysisStatus, GapSeverity, IsoDateTime, Uuid } from '../contract-ai-analysis/contract-ai-analysis.model';

/** The fixed product catalogue of the scan (technical names, see PortfolioProduct in ai-api). */
export type PortfolioProduct =
  | 'PRIVATHAFTPFLICHT'
  | 'KRANKENVERSICHERUNG'
  | 'BERUFSUNFAEHIGKEIT'
  | 'KFZ'
  | 'RISIKOLEBEN'
  | 'KRANKENTAGEGELD'
  | 'TIERHALTERHAFTPFLICHT'
  | 'WOHNGEBAEUDE'
  | 'ELEMENTARSCHADEN'
  | 'HAUSRAT'
  | 'RECHTSSCHUTZ'
  | 'UNFALL'
  | 'PFLEGEZUSATZ';

/** Where a client row came from — only MODEL rows carry a KI badge. */
export type InsightSource = 'PRESCREEN' | 'STORED_ANALYSIS' | 'MODEL';

/** PRIORITY = KI-Score × Erreichbarkeit (default), the rest are the pure keys. */
export type PortfolioClientSort = 'PRIORITY' | 'SCORE' | 'POTENTIAL' | 'CONTRACTS';

/** Which clients the model step analyses; all clients are always pre-screened. */
export interface PortfolioScope {
  /** null = true (default): only clients with a filled-out Finanzcheck. */
  finanzcheckOnly: boolean | null;
  ageFrom: number | null;
  ageTo: number | null;
  /** Finanzcheck profession status names; empty = all. */
  professionStatuses: string[];
  /** Only clients missing at least one of these; empty = all. */
  missingProducts: PortfolioProduct[];
  /** null = false: only clients with at least one banking signal (FIN-7116). */
  bankingSignalsOnly: boolean | null;
}

// ==================== banking signals (FIN-7116) ====================

export type BankingSignalType =
  'FOREIGN_CONTRACT' | 'RENT_WITHOUT_HAUSRAT' | 'CAR_WITHOUT_KFZ' | 'FAMILY_WITHOUT_RISIKOLEBEN';

/** A derived fact from the client's banking data with the German one-liner for the advisor. */
export interface BankingSignal {
  type: BankingSignalType;
  product: PortfolioProduct | null;
  text: string;
  partner: string | null;
  monthlyAmount: number | null;
}

export interface PortfolioRequest {
  clientIds: Uuid[];
  scope: PortfolioScope;
}

/** Result of the free dry run before a scan. */
export interface PortfolioPreview {
  totalClients: number;
  clientsInScope: number;
  clientsWithoutFinanzcheck: number;
  estimatedTokens: number;
  estimatedCredits: number;
  walletRemainingTokens: number;
  sufficient: boolean;
}

export interface ProductCount {
  product: PortfolioProduct;
  label: string;
  count: number;
}

/** One scan: header, KPI tiles and charts of the page. */
export interface PortfolioAnalysis {
  portfolioUid: Uuid;
  advisorId: Uuid;
  status: AnalysisStatus;
  scope: PortfolioScope;
  totalClients: number;
  clientsInScope: number;
  processedClients: number;
  clientsWithoutFinanzcheck: number | null;
  criticalClients: number | null;
  avgContractsPerClient: number | null;
  /** "Gesamtpotenzial" in EUR per year; null before the model step. */
  totalPotentialPerYear: number | null;
  /** "Umsatzpotential" of the whole Bestand: conservative yearly premium over all clients' missing products. */
  totalGapPotentialPerYear: number | null;
  /** "Vertragsverteilung": clients by active contract count, keys "0" … "6" and "7+". */
  contractDistribution: Record<string, number>;
  /** "Häufigste Lücken", descending. */
  topMissingProducts: ProductCount[];
  modelUsed: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  errorMessage: string | null;
  createdAt: IsoDateTime;
  completedAt: IsoDateTime | null;
  /** "N Kunden mit Fremdverträgen" (banking signals); null for scans before FIN-7116. */
  clientsWithForeignContracts: number | null;
}

/** One client's row: card, table row and drawer are rendered from this. */
export interface PortfolioClientInsight {
  clientId: Uuid;
  displayName: string | null;
  age: number | null;
  professionStatus: string | null;
  city: string | null;
  hasFinanzcheck: boolean;
  inScope: boolean;
  activeContracts: number;
  coveredProducts: PortfolioProduct[];
  missingProducts: PortfolioProduct[];
  severity: GapSeverity | null;
  /** KI-Score 0–100 (AI estimate); null without a model answer. */
  score: number | null;
  /** Potenzial / Jahr in EUR (AI estimate); null without a model answer. */
  potentialPerYear: number | null;
  /** "Umsatzpotential": conservative yearly premium over ALL missing products (planning values), every client. */
  gapPotentialPerYear: number | null;
  recommendedProduct: PortfolioProduct | null;
  rationale: string | null;
  productPriorities: PortfolioProduct[];
  source: InsightSource;
  /** "Erreichbarkeit" 0–100 from activity, chat and Finanzcheck signals; null for rows of older scans. */
  warmth: number | null;
  /** German explanations of the warmth ("Zuletzt aktiv im Chat vor 3 Tagen"). */
  warmthReasons: string[];
  /** KI-Score × warmth / 100 — the default "Jetzt handeln" order; null without a model answer. */
  priority: number | null;
  /** Whether the client can be reached in the client app (real login e-mail, not a pool placeholder). */
  reachable: boolean;
  /** Whether the client has an active bank connection. */
  bankConnected: boolean;
  /** Deterministic upselling signals from the banking data; empty without. */
  bankingSignals: BankingSignal[];
  /** Whether the client has a German mobile number, i.e. can be reached by SMS (FIN-7121). */
  smsReachable: boolean;
  /**
   * Whether the client has finished onboarding in the client app (advisor consent accepted, the
   * Kundenliste's "Registriert"). Splits the page into "In App" and "Noch nicht in der App".
   */
  registered: boolean;
}

// ==================== Finanzcheck reminder (FIN-7117) ====================

/** How a reminder reaches the client (FIN-7121): chat message in the app or SMS to the mobile number. */
export type ReminderChannel = 'CHAT' | 'SMS';

export const REMINDER_CHANNEL_LABELS: Record<ReminderChannel, string> = {
  CHAT: 'Chat',
  SMS: 'SMS',
};

/** One SMS part in the GSM-7 alphabet; longer texts are billed as several parts. */
export const SMS_PART_LENGTH = 160;

export interface FinanzcheckReminderCandidate {
  clientId: Uuid;
  displayName: string | null;
  /** Reachable in the client app (real login e-mail). */
  reachable: boolean;
  /** Last reminder on any channel. */
  lastRemindedAt: IsoDateTime | null;
  /** Reachable in the app and outside the cooldown — preselected in the chat picker. */
  remindable: boolean;
  /** Has a German mobile number, i.e. can be reached by SMS (FIN-7121). */
  smsReachable: boolean;
  /** Reminded within the cooldown (on any channel). */
  inCooldown: boolean;
  /** German mobile number (E.164) an SMS reminder goes to, or null — shown in SMS mode for confirmation. */
  mobileNumber: string | null;
}

export interface FinanzcheckReminderOverview {
  candidates: FinanzcheckReminderCandidate[];
  /** Prefilled chat message; `{vorname}` is replaced per client on send. */
  defaultMessage: string;
  cooldownDays: number;
  remindedClients: number;
  convertedClients: number;
  lastSentAt: IsoDateTime | null;
  /** Prefilled SMS text (short); `{vorname}` is replaced per client on send. */
  defaultSmsMessage: string;
}

export interface FinanzcheckReminderRequest {
  clientIds: Uuid[];
  message: string;
  /** CHAT when omitted. */
  channel?: ReminderChannel;
}

export interface FinanzcheckReminderResult {
  runUid: Uuid;
  accepted: number;
  skippedCooldown: number;
  skippedUnreachable: number;
}

/**
 * WebSocket payload of `ai/portfolio-reminder-progress|complete` (Finanzcheck reminder) and
 * `ai/portfolio-message-progress|complete` (segment message).
 */
export interface PortfolioReminderEvent {
  runUid: Uuid;
  /** Channel of the run (FIN-7121); missing on events of older backends. */
  channel?: ReminderChannel;
  sent: number;
  failed: number;
  total: number;
}

// ==================== segment message (FIN-7118) ====================

export interface PortfolioMessageCandidate {
  clientId: Uuid;
  displayName: string | null;
  reachable: boolean;
  lastSentAt: IsoDateTime | null;
  /** Reachable and outside the per-product cooldown — preselected in the composer. */
  sendable: boolean;
}

export interface PortfolioMessagePreview {
  candidates: PortfolioMessageCandidate[];
  /** Prefilled text for the product; `{vorname}` is replaced per client on send. */
  defaultMessage: string;
  cooldownDays: number;
  maxRecipients: number;
}

export interface PortfolioMessagePreviewRequest {
  clientIds: Uuid[];
  product: PortfolioProduct | null;
}

export interface PortfolioMessageRequest {
  clientIds: Uuid[];
  product: PortfolioProduct | null;
  message: string;
}

export interface PortfolioMessageResult {
  runUid: Uuid;
  accepted: number;
  skippedCooldown: number;
  skippedUnreachable: number;
  skippedLimit: number;
}

/** WebSocket payload of `ai/portfolio-progress`. */
export interface PortfolioProgressEvent {
  portfolioUid: Uuid;
  phase: 'PRESCREEN' | 'MODEL';
  processedClients: number;
  totalClients: number;
  clientsInScope: number;
}

/** WebSocket payload of `ai/portfolio-complete`. */
export interface PortfolioCompleteEvent {
  portfolioUid: Uuid;
  status: AnalysisStatus;
}

/** German labels of the product catalogue, in catalogue rank order. */
export const PORTFOLIO_PRODUCT_LABELS: Record<PortfolioProduct, string> = {
  PRIVATHAFTPFLICHT: 'Privathaftpflicht',
  KRANKENVERSICHERUNG: 'Krankenversicherung',
  BERUFSUNFAEHIGKEIT: 'Berufsunfähigkeit',
  KFZ: 'KFZ',
  RISIKOLEBEN: 'Risikoleben',
  KRANKENTAGEGELD: 'Krankentagegeld',
  TIERHALTERHAFTPFLICHT: 'Tierhalterhaftpflicht',
  WOHNGEBAEUDE: 'Wohngebäude',
  ELEMENTARSCHADEN: 'Elementarschaden',
  HAUSRAT: 'Hausrat',
  RECHTSSCHUTZ: 'Rechtsschutz',
  UNFALL: 'Unfall',
  PFLEGEZUSATZ: 'Pflegeversicherung',
};

export const PORTFOLIO_PRODUCTS: PortfolioProduct[] = Object.keys(PORTFOLIO_PRODUCT_LABELS) as PortfolioProduct[];

/** German labels of the Finanzcheck profession statuses (account service enum). */
export const PROFESSION_STATUS_LABELS: Record<string, string> = {
  EMPLOYED: 'Angestellt',
  CONTRACTOR: 'Selbstständig',
  STUDENT: 'Student',
  TRAINEE: 'Azubi',
  CIVIL_SERVANT: 'Beamter',
  PUBLIC_SERVICE: 'Öffentlicher Dienst',
  NO_EMPLOYMENT: 'Ohne Beschäftigung',
  PENSIONER: 'Rentner',
};

export const SEVERITY_LABELS: Record<GapSeverity, string> = {
  CRITICAL: 'Kritisch',
  MEDIUM: 'Mittel',
  LOW: 'Niedrig',
};

/** How many active contracts count as "fully covered" in the Absicherung bar (n/10 Verträge). */
export const COVERAGE_BAR_MAX = 10;

/** A KI-Score at or above this counts as "hohe Abschlusswahrscheinlichkeit". */
export const HIGH_SCORE_THRESHOLD = 80;

export type WarmthLevel = 'high' | 'mid' | 'low' | 'none';

export const WARMTH_LABELS: Record<WarmthLevel, string> = {
  high: 'Gut erreichbar',
  mid: 'Erreichbar',
  low: 'Schwer erreichbar',
  none: 'Erreichbarkeit unbekannt',
};
